import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Building2, ArrowLeft } from "lucide-react";

export default function RegisterAccountPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ business_name: "", admin_name: "", admin_email: "", admin_password: "", phone: "", address: "" });
  const [confirmPw, setConfirmPw] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!form.business_name.trim()) { toast.error("Business name is required"); return; }
    if (!form.admin_name.trim()) { toast.error("Your name is required"); return; }
    if (!form.admin_email.trim()) { toast.error("Email is required"); return; }
    if (form.admin_password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (form.admin_password !== confirmPw) { toast.error("Passwords don't match"); return; }
    setLoading(true);
    try {
      const res = await api.post("/register-account", form);
      localStorage.setItem("token", res.data.session_token);
      toast.success(`Welcome! ${res.data.account.business_name} account created.`);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="register-account-page">
      <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 w-full max-w-md">
        <button onClick={() => navigate("/login")} className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 mb-4">
          <ArrowLeft className="h-3 w-3" /> Back to login
        </button>
        <div className="text-center mb-6">
          <div className="h-12 w-12 bg-[var(--brand-primary)] rounded-xl flex items-center justify-center mx-auto mb-3">
            <Building2 className="h-6 w-6 text-white" />
          </div>
          <h1 className="text-xl font-bold text-slate-900">Create Your Account</h1>
          <p className="text-sm text-slate-500 mt-1">Start your free trial of CashUpPOS</p>
        </div>

        <div className="space-y-3">
          <div>
            <Label>Business Name *</Label>
            <Input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} placeholder="Your Restaurant or Store" className="mt-1" data-testid="reg-business-name" />
          </div>
          <div>
            <Label>Your Name *</Label>
            <Input value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} placeholder="John Smith" className="mt-1" data-testid="reg-admin-name" />
          </div>
          <div>
            <Label>Email *</Label>
            <Input type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} placeholder="you@business.com" className="mt-1" data-testid="reg-admin-email" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Password *</Label>
              <Input type="password" value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} placeholder="Min 6 characters" className="mt-1" data-testid="reg-admin-password" />
            </div>
            <div>
              <Label>Confirm Password</Label>
              <Input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} placeholder="Confirm" className="mt-1" data-testid="reg-confirm-password" />
            </div>
          </div>
          <div>
            <Label>Phone (optional)</Label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(555) 123-4567" className="mt-1" data-testid="reg-phone" />
          </div>
          <Button onClick={handleSubmit} disabled={loading} className="w-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white mt-2" data-testid="reg-submit-btn">
            {loading ? "Creating Account..." : "Start Free Trial"}
          </Button>
        </div>
        <p className="text-[10px] text-slate-400 text-center mt-4">By signing up you agree to the CashUpPOS Terms of Service</p>
      </div>
    </div>
  );
}
